import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../core/_base/crud';
import { TranslateService } from '@ngx-translate/core';
import { PrescriptionEditComponent } from '../prescription-edit/prescription-edit.component';
import {
  ClearPrescriptionState,
  Drug,
  Prescription,
  PrescriptionActionTypes,
  PrescriptionOnServerDeleted,
  PrescriptionsDataSource,
  PrescriptionService,
  PrescriptionsPageRequested,
  selectPrescriptionError,
  selectPrescriptionLatestSuccessfullAction,
  selectPrescriptionsPageLastQuery,
} from 'src/app/core/lists';
import { ActivatedRoute } from '@angular/router';
import { DrpComponent } from '../../../../../med-review/drp/drp.component';
import { CustomDrpComponent } from '../../../../../med-review/drp/custom-drp/custom-drp.component';
import { MatSelect } from '@angular/material/select';
import { Moment, utc } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { GeneralValidationMessagesService } from '../../../../../../../core/services/general-validation-messages.service';
import { MedicationHistoryComponent } from '../../../../../med-review/components/medication-history/medication-history.component';
import { GeneralActionDialogComponent } from '../../../../../../partials/content/crud/general-action-dialog/general-action-dialog.component';
import { PrescriptionNoteComponent } from '../prescription-note/prescription-note.component';
import { AuthService, User } from 'src/app/core/auth';
import { DownloadService } from '../../../../../../../shared/utils/download.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-prescriptions-list',
  templateUrl: './prescriptions-list.component.html',
  styleUrls: ['./prescriptions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionsListComponent implements OnInit, OnDestroy {
  patient;
  patientId: number;
  dataSource: PrescriptionsDataSource;
  displayedColumns = [
    'drug',
    'rx',
    'doctor',
    'prescriptionDate',
    'lastFilledDate',
    'daysSupply',
    'refill',
    'hold',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchDrug',
    'searchRx',
    'searchDoctor',
    'searchPrescriptionDate',
    'searchLastFilledDate',
    'searchDaysSupply',
    'searchRefill',
    'searchHold',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchDrugInput') searchDrugInput: ElementRef;
  @ViewChild('searchRxInput') searchRxInput: ElementRef;
  @ViewChild('searchDoctorInput') searchDoctorInput: ElementRef;
  @ViewChild('searchPrescriptionDateInput') searchPrescriptionDateInput: ElementRef;
  @ViewChild('searchPrescriptionDatePicker') searchPrescriptionDatePicker: MatDatepicker<Moment>;
  @ViewChild('searchLastFilledDateInput') searchLastFilledDateInput: ElementRef;
  @ViewChild('searchLastFilledDatePicker') searchLastFilledDatePicker: MatDatepicker<Moment>;
  @ViewChild('searchDaysSupplyInput') searchDaysSupplyInput: ElementRef;
  @ViewChild('searchRefillInput') searchRefillInput: ElementRef;
  @ViewChild('searchHoldSelect') searchHoldSelect: MatSelect;
  @ViewChild('searchIsActiveSelect') searchIsActiveSelect: MatSelect;
  statusTracker = 0;
  filterStatus = '';
  lastQuery: QueryParamsModel;
  selection = new SelectionModel<Prescription>(true, []);
  prescriptionsResult: Prescription[] = [];
  users: any[];
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: Subscription[] = [];
  filterCompany = '';
  public isReadOnly = false;
  @Input() fromPatient: any;
  @Input() patient_id_for_task_list: number;
  currentUser: User;

  constructor(
    public dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private prescriptionService: PrescriptionService,
    private globalService: GeneralValidationMessagesService,
    private downloadService: DownloadService,
    private auth: AuthService,
  ) {}

  ngOnInit() {
    this.auth.getUserByToken().subscribe((user) => {
      this.currentUser = user;
    });
    this.dataSource = new PrescriptionsDataSource(this.store);
    const dataSourceSub = this.dataSource.entitySubject.subscribe(
      (res) => (this.prescriptionsResult = res),
    );
    this.subscriptions.push(dataSourceSub);
    this.activatedRoute.params.subscribe((params) => {
      this.patientId = this.patient_id_for_task_list ? this.patient_id_for_task_list : params.id;
      if (!this.fromPatient) return;
      setTimeout(() => this.loadPrescriptionsList());
      this.globalService.getEntitiesById('patients', this.patientId).subscribe((result) => {
        if (result) {
          this.patient = result.data;
        }
      });
    });
    const errorSubscription = this.store
      .pipe(select(selectPrescriptionError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Delete,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectPrescriptionLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg: string;
          let msgType: MessageType;
          switch (res.action_type) {
            case PrescriptionActionTypes.PrescriptionCreated:
              successMsg = this.translate.instant('PRESCRIPTION.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case PrescriptionActionTypes.PrescriptionUpdated:
              successMsg = this.translate.instant('PRESCRIPTION.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case PrescriptionActionTypes.PrescriptionDeleted:
              successMsg = this.translate.instant(
                'PRESCRIPTION.DELETE_PRESCRIPTION_SIMPLE.MESSAGE',
              );
              msgType = MessageType.Delete;
              break;
          }
          this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
        }
      });
    this.subscriptions.push(successSubscription);
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push(sortSubscription);
    /* Data load will be triggered in two cases:
    - when a pagination prescription occurs => this.paginator.page
    - when a sort prescription occurs => this.sort.sortChange
    **/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadPrescriptionsList()))
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);
    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadPrescriptionsList();
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);
    const lastQuerySubscription = this.store
      .pipe(select(selectPrescriptionsPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push(lastQuerySubscription);
    if (
      this.activatedRoute.url[`_value`][1] &&
      this.activatedRoute.url[`_value`][1].path === 'view'
    ) {
      this.isReadOnly = true;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(new ClearPrescriptionState());
  }

  loadPrescriptionsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(new PrescriptionsPageRequested({ page: queryParams }));
    this.selection.clear();
  }

  formatDate(date: string): string {
    if (!date) return null;

    const dateList = date.split('-');

    dateList.unshift(dateList.pop());

    return dateList.join('-');
  }

  filterConfiguration(): any {
    const filter: any = {};

    if (this.searchIsActiveSelect?.value === undefined) this.searchIsActiveSelect.value = 'true';

    filter.isActive = this.searchIsActiveSelect?.value;
    filter.patientId = this.patientId;

    if (this.showSearchBar) {
      filter.drug = this.searchDrugInput?.nativeElement?.value;
      filter.rx = this.searchRxInput?.nativeElement?.value;
      filter.doctor = this.searchDoctorInput?.nativeElement?.value;
      filter.prescriptionDate = this.formatDate(
        this.searchPrescriptionDateInput?.nativeElement?.value,
      );
      filter.lastFilledDate = this.formatDate(this.searchLastFilledDateInput?.nativeElement?.value);
      filter.daysSupply = this.searchDaysSupplyInput?.nativeElement?.value;
      filter.refill = this.searchRefillInput?.nativeElement?.value;
      filter.hold = this.searchHoldSelect?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  deletePrescription(_item: Prescription) {
    const _title: string = this.translate.instant('PRESCRIPTION.DELETE_PRESCRIPTION_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'PRESCRIPTION.DELETE_PRESCRIPTION_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'PRESCRIPTION.DELETE_PRESCRIPTION_SIMPLE.WAIT_DESCRIPTION',
    );
    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(
        new PrescriptionOnServerDeleted({ id: _item.id, patientId: this.patientId }),
      );
    });
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  addPrescription() {
    const newPrescription = new Prescription();
    this.updatePrescription(newPrescription);
  }

  updatePrescription(prescription: Prescription) {
    this.dialog.open(PrescriptionEditComponent, {
      width: '40vw',
      data: { prescription, patientId: this.patientId, isReadOnly: false },
    });
  }

  viewPrescription(prescription: Prescription) {
    this.dialog.open(PrescriptionEditComponent, {
      width: '40vw',
      data: { prescription, patientId: this.patientId, isReadOnly: true },
    });
  }

  getDrug(drug: Drug) {
    let result: string;
    if (drug.ln60) {
      result = drug.ln60;
      return result;
    }
    const name = drug.drug ? drug.drug : '';
    const strength = drug.strength ? drug.strength : '';
    const form = drug.form ? drug.form : '';
    result = name + ' ' + strength + ' ' + form;
    return result;
  }

  openDrpDialog() {
    this.dialog.open(DrpComponent, {
      data: {
        patient: this.patient,
      },
      width: '95vw',
      maxWidth: '95vw',
      height: '95vh',
    });
  }

  customDRP(prescription: Prescription) {
    const NDC = prescription?.drug?.ndc;
    if (NDC === '' || !NDC) {
      this.dialog.open(GeneralActionDialogComponent, {
        width: '400px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          description: `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">Unable to create DRP. You need to fill the NDC for the related drug.</p>
            </div>
          `,
        },
      });
      return;
    }
    this.dialog.open(CustomDrpComponent, {
      data: {
        patient: this.patient,
        prescription,
        from: 'PRESCRIPTIONS_LIST',
        facility_cycle_date: this.patient.facility_cycle_date,
      },
      width: '85vw',
      maxWidth: '85vw',
      height: '95vh',
    });
  }

  reloadPrescriptionsList(): void {
    this.paginator.pageIndex = 0;

    this.loadPrescriptionsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect.value = 'true';
        break;
      case 1:
        this.searchIsActiveSelect.value = 'false';
        break;
      case 2:
        this.searchIsActiveSelect.value = '';
        break;
    }

    this.reloadPrescriptionsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect.value) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchDrugInput.nativeElement.value = '';
    this.searchRxInput.nativeElement.value = '';
    this.searchDoctorInput.nativeElement.value = '';
    this.searchPrescriptionDateInput.nativeElement.value = '';
    this.searchPrescriptionDatePicker?.select(undefined);
    this.searchLastFilledDateInput.nativeElement.value = '';
    this.searchLastFilledDatePicker?.select(undefined);
    this.searchDaysSupplyInput.nativeElement.value = '';
    this.searchRefillInput.nativeElement.value = '';
    this.searchHoldSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadPrescriptionsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchDrugInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadPrescriptionsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadPrescriptionsList();
  }

  exportPrescriptionsAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.prescriptionService
      .getPrescriptionsAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Prescriptions List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }

  async updateNotesPrescription(prescription: Prescription) {
    try {
      this.dialog.open(PrescriptionNoteComponent, {
        width: 'auto',
        data: { prescription, isReadOnly: false, currentUser: this.currentUser },
      });
    } catch (error) {
      // TODO: Handle error gracefully
    }
  }

  showHistory(prescription: Prescription) {
    this.dialog.open(MedicationHistoryComponent, {
      data: { medication: prescription.drug },
      width: '700px',
    });
  }
}
